import { isInteractionLocked } from "../ui/interaction-lock.js";
import { toAriaKeyShortcut, toDisplayKeyShortcut } from "./aria.js";
import { matchesHotkeyEvent, parseHotkeyPattern } from "./hotkey.js";
import { isEditableTarget, isInteractiveActivation, listen } from "@videojs/utils/dom";
import { isUndefined } from "@videojs/utils/predicate";
//#region src/dom/hotkey/coordinator.ts
var HotkeyCoordinator = class {
	#target;
	#bindings = [];
	#nextId = 0;
	#disconnect = null;
	#docDisconnect = null;
	#activationSubscribers = /* @__PURE__ */ new Set();
	#shortcutSubscribers = /* @__PURE__ */ new Set();
	#destroyed = false;
	constructor(target) {
		this.#target = target;
	}
	subscribe(callback) {
		this.#activationSubscribers.add(callback);
		return () => this.#activationSubscribers.delete(callback);
	}
	subscribeShortcutChanges(callback) {
		this.#shortcutSubscribers.add(callback);
		return () => this.#shortcutSubscribers.delete(callback);
	}
	add(options) {
		const binding = {
			parsed: parseHotkeyPattern(options.keys),
			options,
			id: this.#nextId++
		};
		this.#bindings.push(binding);
		this.#sortBindings();
		if (options.target === "document") this.#connectDocument();
		else this.#connect();
		this.#notify();
		let removed = false;
		return () => {
			if (removed) return;
			removed = true;
			const idx = this.#bindings.indexOf(binding);
			if (idx !== -1) this.#bindings.splice(idx, 1);
			this.#maybeDisconnect();
			this.#notify();
		};
	}
	getAriaKeys(action) {
		return this.getShortcut(action).aria;
	}
	getShortcut(action, value) {
		const bindings = this.#getActionBindings(action, value);
		if (!bindings.length) return {};
		const parsed = bindings.flatMap((binding) => binding.parsed);
		const preferred = bindings[bindings.length - 1];
		return {
			aria: toAriaKeyShortcut(parsed),
			shortcut: this.#formatDisplayShortcut(preferred)
		};
	}
	destroy() {
		if (this.#destroyed) return;
		this.#destroyed = true;
		this.#disconnect?.abort();
		this.#disconnect = null;
		this.#docDisconnect?.abort();
		this.#docDisconnect = null;
		this.#bindings = [];
		this.#notify();
		this.#activationSubscribers.clear();
		this.#shortcutSubscribers.clear();
	}
	#sortBindings() {
		this.#bindings.sort((a, b) => {
			const specDiff = b.parsed[0].modifiers.size - a.parsed[0].modifiers.size;
			if (specDiff !== 0) return specDiff;
			return a.id - b.id;
		});
	}
	#connect() {
		if (this.#disconnect) return;
		this.#disconnect = new AbortController();
		listen(this.#target, "keydown", this.#handleEvent, { signal: this.#disconnect.signal });
	}
	#connectDocument() {
		if (this.#docDisconnect) return;
		this.#docDisconnect = new AbortController();
		listen(document, "keydown", this.#handleEvent, { signal: this.#docDisconnect.signal });
	}
	#maybeDisconnect() {
		const hasPlayer = this.#bindings.some((b) => b.options.target !== "document");
		const hasDoc = this.#bindings.some((b) => b.options.target === "document");
		if (!hasPlayer) {
			this.#disconnect?.abort();
			this.#disconnect = null;
		}
		if (!hasDoc) {
			this.#docDisconnect?.abort();
			this.#docDisconnect = null;
		}
	}
	#handleEvent = (event) => {
		if (isInteractionLocked(this.#target)) return;
		if (event.key === "Unidentified") return;
		if (isInteractiveActivation(event)) return;
		if (event.defaultPrevented) return;
		const editable = isEditableTarget(event);
		for (const binding of this.#bindings) {
			const { options, parsed } = binding;
			if (options.disabled) continue;
			if (event.repeat && options.repeatable === false) continue;
			if (options.target === "document" !== (event.currentTarget === document)) continue;
			for (const p of parsed) {
				if (!matchesHotkeyEvent(p, event)) continue;
				if (editable && p.modifiers.size === 0) continue;
				if (this.#activationSubscribers.size > 0) {
					const activateEvent = {
						source: "hotkey",
						action: options.action,
						value: options.value,
						event
					};
					for (const cb of this.#activationSubscribers) try {
						cb(activateEvent);
					} catch (error) {
						console.warn("[vjs-hotkey] subscribe callback threw:", error);
					}
				}
				event.preventDefault();
				options.onActivate(event, p.originalKey);
				return;
			}
		}
	};
	#getActionBindings(action, value) {
		return this.#bindings.filter((binding) => {
			if (binding.options.disabled) return false;
			if (binding.options.action !== action) return false;
			if (isUndefined(value)) return true;
			return binding.options.value === value;
		}).sort((a, b) => a.id - b.id);
	}
	#formatDisplayShortcut(binding) {
		if (binding.options.keys === "0-9") return binding.options.keys;
		return toDisplayKeyShortcut(binding.parsed[0]);
	}
	#notify() {
		for (const subscriber of this.#shortcutSubscribers) subscriber();
	}
};
//#endregion
export { HotkeyCoordinator };

//# sourceMappingURL=coordinator.js.map